import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { garageAPI } from '../../services/garage/api';
import { parseTimestamp } from '../../utils/dateFormatter';
import './NotificationBell.css';

const NotificationBell = ({ userEmail, onNotificationNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Load notifications and unread count
  useEffect(() => {
    if (userEmail) {
      loadNotifications();
      loadUnreadCount();
      
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        loadUnreadCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [userEmail]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await garageAPI.getNotifications(userEmail, false, 20);
      setNotifications(data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const data = await garageAPI.getUnreadCount(userEmail);
      setUnreadCount(data.count || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      loadNotifications();
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      if (!notification.isRead) {
        await garageAPI.markNotificationAsRead(notification.id, userEmail);
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      // Navigate to the action URL
      if (notification.actionUrl) {
        // Check if the URL is for AI Garage request detail (/ai-garage/request/:id)
        const requestMatch = notification.actionUrl.match(/\/ai-garage\/request\/([a-f0-9-]+)/);
        if (requestMatch && onNotificationNavigate) {
          // Use the provided navigation callback for internal navigation
          const requestId = requestMatch[1];
          onNotificationNavigate(requestId);
        } else {
          // Fallback to full page navigation
          window.location.href = notification.actionUrl;
        }
      }

      setShowDropdown(false);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await garageAPI.markAllNotificationsAsRead(userEmail);
      setUnreadCount(0);
      await loadNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'status_change': return '🔄';
      case 'new_comment': return '💬';
      case 'request_info': return 'ℹ️';
      case 'assigned': return '👤';
      case 'it_routed': return '🔧';
      case 'roi_config_change': return '⏱️';
      default: return '📢';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = parseTimestamp(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (!userEmail) return null;

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell-button" 
        onClick={handleBellClick}
        aria-label={`${unreadCount} unread notifications`}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllAsRead} className="mark-all-read-btn">
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">No notifications yet</div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`notification-item ${notif.isRead ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="notification-icon">{getNotificationIcon(notif.notificationType)}</div>
                  <div className="notification-content">
                    <div className="notification-title">{notif.title}</div>
                    <div className="notification-message">{notif.message}</div>
                    <div className="notification-time">{formatTimeAgo(notif.createdAt)}</div>
                  </div>
                  {!notif.isRead && <div className="notification-unread-dot"></div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
